import { motion } from "framer-motion";
import { useI18n } from "../../i18n";
import { EASE } from "../../lib/site";
import { Reveal, SectionLabel } from "./Reveal";

const Tile = ({ src, children, i = 0 }) => (
  <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-ink/10 bg-white p-1.5 shadow-sm">
    {src ? (
      <img src={src} alt="" loading="lazy" className="max-h-full max-w-full object-contain" />
    ) : (
      children
    )}
  </span>
);

const EDU_LOGOS = ["/img/logo-lse.png", "/img/logo-hkbu.png"];
const EXP_LOGOS = ["/img/logo-bdo.png", "/img/logo-ceiec.png", "/img/logo-bnbu.png"];

const Edu = ({ e, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, ease: EASE, delay: i * 0.1 }}
    whileHover={{ y: -4 }}
    data-testid={`education-item-${i}`}
    className="flex items-start gap-5 rounded-2xl border border-ink/10 bg-white p-6"
  >
    <Tile src={EDU_LOGOS[i]} />
    <div className="min-w-0">
      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-terra">{e.period}</div>
      <h4 className="mt-1.5 font-serif text-xl leading-snug text-ink">{e.school}</h4>
      {e.schoolSub && <div className="mt-2 text-xs leading-relaxed text-muted-ink">{e.schoolSub}</div>}
      <div className="mt-2 text-sm font-medium text-body-ink">{e.degree}</div>
      <ul className="mt-[10px] space-y-2">
        {e.points.map((p) => (
          <li key={p} className="flex gap-2 text-sm leading-relaxed text-muted-ink"><span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-gold" />{p}</li>
        ))}
      </ul>
    </div>
  </motion.div>
);

const Exp = ({ x, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, ease: EASE, delay: i * 0.1 }}
    whileHover={{ y: -4 }}
    data-testid={`experience-card-${i}`}
    className="flex items-start gap-5 rounded-2xl border border-ink/10 bg-white p-7"
  >
    <Tile src={EXP_LOGOS[i]} />
    <div className="min-w-0">
      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-terra">{x.period}</div>
      <h4 className="mt-2.5 font-serif text-xl leading-snug text-ink">{x.org}</h4>
      <div className="mt-1 text-[15px] font-medium leading-[1.65] text-body-ink">{x.role}</div>
      <ul className="mt-4 max-w-[34rem] space-y-2 pl-2">
        {x.points.map((p) => (
          <li key={p} className="flex gap-3 text-sm leading-relaxed text-muted-ink"><span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-gold" />{p}</li>
        ))}
      </ul>
    </div>
  </motion.div>
);

export const Journey = () => {
  const { t } = useI18n();
  const j = t.journey;
  return (
    <section id="journey" data-testid="journey-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-12 lg:py-36">
      <Reveal><SectionLabel>{j.label}</SectionLabel></Reveal>
      <Reveal delay={0.1}><h2 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.05] text-ink sm:text-5xl lg:text-6xl">{j.title}</h2></Reveal>
      <div className="mt-16 grid grid-cols-1 gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <h3 className="mb-6 font-mono text-xs uppercase tracking-[0.28em] text-muted-ink">{j.eduLabel}</h3>
          <div className="space-y-4">
            {j.edu.map((e, i) => <Edu key={e.school} e={e} i={i} />)}
          </div>
        </div>
        <div className="lg:col-span-7">
          <h3 className="mb-8 font-mono text-xs uppercase tracking-[0.28em] text-muted-ink">{j.expLabel}</h3>
          <div className="grid grid-cols-1 gap-5">
            {j.exp.map((x, i) => <Exp key={x.org} x={x} i={i} />)}
          </div>
        </div>
      </div>
    </section>
  );
};
